import React, { ElementType, useCallback, forwardRef, Ref } from 'react'
import { cn, UtilityClassNames } from '@s4l/utility-css'
import Typography, { type TypographySizes, type TypographyVariant } from '../Typography'
import styles from './index.module.css'

type TextLinkSize = 'extraLarge' | 'large' | 'medium' | 'small'
type TextLinkVariant = 'on-light' | 'on-dark' | 'neutral'
type ValidVariantMapping<V extends TypographyVariant> = {
  variant: V
  size: TypographySizes<V>
}
type BodyMapping = ValidVariantMapping<'body'>
type CaptionMapping = ValidVariantMapping<'caption'>
type TextLinkTarget = '_blank' | '_self' | '_parent' | '_top' | 'framename'

type TextLinkOwnProps<E extends ElementType = 'a'> = {
  className?: UtilityClassNames
  children?: React.ReactNode
  size?: TextLinkSize
  variant?: TextLinkVariant
  hover?: boolean
  active?: boolean
  focus?: boolean
  visited?: boolean
  bold?: boolean
  href?: string
  target?: TextLinkTarget
  as?: E
}

export type TextLinkProps<E extends ElementType = 'a'> = TextLinkOwnProps<E> &
  Omit<React.ComponentPropsWithoutRef<E>, keyof TextLinkOwnProps>

function TextLinkComponent<E extends ElementType = 'a'>(
  {
    className,
    children,
    size = 'large',
    variant = 'on-light',
    hover,
    active,
    focus,
    visited,
    bold,
    as,
    target = '_self',
    ...props
  }: TextLinkProps<E>,
  ref: Ref<E extends 'a' ? HTMLAnchorElement : E extends 'button' ? HTMLButtonElement : HTMLElement>
) {
  const innerOnClick = useCallback(
    (evt: React.MouseEvent<E extends 'a' ? HTMLAnchorElement : HTMLButtonElement>) => {
      props.onClick?.(evt)
    },
    [props]
  )

  const Component = as ?? ('a' as ElementType)

  if (Component === 'a' && !props.href) {
    return null
  }

  const state = [hover && 'hover', active && 'active', focus && 'focus', visited && 'visited']
    .filter(Boolean)
    .join(' ')

  const linkClassName = `${styles.link} ${styles[variant]}`

  const typographySizeMapping: Record<TextLinkSize, BodyMapping | CaptionMapping> = {
    extraLarge: { variant: 'body', size: 'xl' },
    large: { variant: 'body', size: 'l' },
    medium: { variant: 'caption', size: 'l' },
    small: { variant: 'caption', size: 'm' },
  }

  const typographyProps = typographySizeMapping[size]

  const commonProps = {
    className: cn(linkClassName, className),
    type: Component === 'button' ? 'button' : undefined,
    'data-state': state,
    ...props,
    onClick: innerOnClick,
    href: Component === 'a' ? props.href : undefined,
    target: Component === 'a' ? target : undefined,
    rel: Component === 'a' && target === '_blank' ? 'noopener noreferrer' : props.rel,
    ref,
  }

  return (
    <Component {...commonProps}>
      <Typography {...typographyProps} bold={bold} data-testid="typography">
        {children}
      </Typography>
    </Component>
  )
}

const TextLink = forwardRef(TextLinkComponent) as <E extends ElementType = 'a'>(
  props: TextLinkProps<E> & {
    ref?: Ref<
      E extends 'a' ? HTMLAnchorElement : E extends 'button' ? HTMLButtonElement : HTMLElement
    >
  }
) => ReturnType<typeof TextLinkComponent>

export default TextLink
